import * as Yup from 'yup';

// Common validation schemas
export const requiredString = Yup.string().required('This field is required');
export const requiredNumber = Yup.number().required('This field is required');
export const requiredDate = Yup.date().required('This field is required');
export const requiredEmail = Yup.string().email('Invalid email format').required('Email is required');
export const requiredUrl = Yup.string().url('Invalid URL format').required('URL is required');

/**
 * Standard validation functions for multi-step forms
 * These can be used without Yup for simple validations
 */

/**
 * Validates if a value is not empty
 * @param {*} value - The value to check
 * @returns {boolean} - True if value is not empty
 */
export const isNotEmpty = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
};

/**
 * Validates if a value is a valid email
 * @param {string} email - The email to validate
 * @returns {boolean} - True if email is valid
 */
export const isValidEmail = (email) => {
  if (!email) return false;
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

/**
 * Validates if a value is a valid URL
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if URL is valid
 */
export const isValidUrl = (url) => {
  if (!url) return false;
  try {
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Validates if a value is a valid phone number
 * @param {string} phone - The phone number to validate
 * @returns {boolean} - True if phone number is valid
 */
export const isValidPhone = (phone) => {
  if (!phone) return false;
  // Basic phone validation - can be enhanced with country-specific rules
  const re = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/;
  return re.test(String(phone));
};

/**
 * Validates if a date is not in the past
 * @param {Date|string} date - The date to validate
 * @returns {boolean} - True if date is not in the past
 */
export const isNotPastDate = (date) => {
  if (!date) return false;
  const dateObj = date instanceof Date ? date : new Date(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return dateObj >= today;
};

/**
 * Validates if an end date is after a start date
 * @param {Date|string} startDate - The start date
 * @param {Date|string} endDate - The end date
 * @returns {boolean} - True if end date is after start date
 */
export const isEndDateAfterStartDate = (startDate, endDate) => {
  if (!startDate || !endDate) return false;
  const startDateObj = startDate instanceof Date ? startDate : new Date(startDate);
  const endDateObj = endDate instanceof Date ? endDate : new Date(endDate);
  return endDateObj > startDateObj;
};

/**
 * Validates if a number is positive
 * @param {number} num - The number to check
 * @returns {boolean} - True if number is positive
 */
export const isPositiveNumber = (num) => {
  if (num === null || num === undefined) return false;
  return Number(num) > 0;
};

/**
 * Validates if a collection has at least n items
 * @param {Array} collection - The array to check
 * @param {number} minCount - Minimum number of items (default: 1)
 * @returns {boolean} - True if collection has at least minCount items
 */
export const hasMinItems = (collection, minCount = 1) => {
  if (!Array.isArray(collection)) return false;
  return collection.length >= minCount;
};

/**
 * Gets a nested property from an object using a dot-notation path
 * @param {Object} obj - The object to get the property from
 * @param {string} path - The property path in dot notation (e.g., 'user.address.city')
 * @returns {*} - The value at the specified path or undefined if not found
 */
export const getNestedValue = (obj, path) => {
  if (!obj || !path) return undefined;
  const keys = path.split('.');
  let result = obj;
  for (const key of keys) {
    if (result === null || result === undefined || typeof result !== 'object') return undefined;
    result = result[key];
  }
  return result;
};

/**
 * Validates a step in multi-step form using validation rules
 * @param {Object} formData - The form data object
 * @param {Object} validationRules - Object mapping field paths to validation rules
 * @param {Object} fieldsToTouch - Object to populate with fields that should be marked as touched
 * @returns {Object} - Object with errors and isValid flag
 */
export const validateFormStep = (formData, validationRules, fieldsToTouch = {}) => {
  if (!formData || !validationRules) {
    return { errors: {}, isValid: false };
  }
  
  const errors = {};
  let isValid = true;
  
  // Process each validation rule
  Object.entries(validationRules).forEach(([fieldPath, rule]) => {
    const fieldValue = getNestedValue(formData, fieldPath);
    
    // Skip validation for optional fields that are empty
    if (rule.optional && !isNotEmpty(fieldValue)) {
      return;
    }
    
    // Check required fields
    if (rule.required && !isNotEmpty(fieldValue)) {
      errors[fieldPath] = rule.message || `This field is required`;
      fieldsToTouch[fieldPath] = true;
      isValid = false;
      return;
    }
    
    // Run validator function if provided
    if (rule.validator && fieldValue !== undefined) {
      // Skip empty optional fields
      if (!rule.optional || isNotEmpty(fieldValue)) {
        const isValidField = rule.validator(fieldValue, formData);
        if (!isValidField) {
          errors[fieldPath] = rule.message || `This field is invalid`;
          fieldsToTouch[fieldPath] = true;
          isValid = false;
        }
      }
    }
  });
  
  return { errors, isValid };
};

// Form field validation schemas
export const brandValidation = Yup.object({
  name: requiredString,
  description: Yup.string(),
  logo: Yup.mixed(),
  website: Yup.string().url('Invalid URL format'),
  industry: Yup.string(),
  foundedYear: Yup.number().nullable().typeError('Year must be a number'),
  services: Yup.array(),
  voice: Yup.object().shape({
    tone: Yup.string(),
    style: Yup.string(),
    personality: Yup.string(),
  }),
  colors: Yup.object().shape({
    primary: Yup.string(),
    secondary: Yup.string(),
    accent: Yup.string(),
  }),
});

export const clientValidation = Yup.object({
  name: requiredString,
  email: requiredEmail,
  phone: Yup.string(),
  address: Yup.string(),
  contactPerson: Yup.object().shape({
    name: Yup.string(),
    role: Yup.string(),
    email: Yup.string().email('Invalid email format'),
    phone: Yup.string(),
  }),
  status: Yup.string(),
});

export const projectValidation = Yup.object({
  name: requiredString,
  description: Yup.string(),
  startDate: requiredDate,
  endDate: Yup.date().min(
    Yup.ref('startDate'),
    'End date must be after start date'
  ),
  budget: Yup.number().positive('Budget must be positive'),
  clientId: requiredString,
  brandId: requiredString,
  status: Yup.string(),
  priority: Yup.string(),
});

export const campaignValidation = Yup.object({
  name: requiredString,
  description: Yup.string(),
  startDate: requiredDate,
  endDate: Yup.date().min(
    Yup.ref('startDate'),
    'End date must be after start date'
  ),
  budget: Yup.number().positive('Budget must be positive'),
  clientId: requiredString,
  brandId: requiredString,
  platforms: Yup.array(),
  status: Yup.string(),
  kpis: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required('KPI name is required'),
      target: Yup.number().required('Target value is required'),
      unit: Yup.string(),
    })
  ),
});

export const contentValidation = Yup.object({
  title: requiredString,
  type: requiredString,
  description: Yup.string(),
  campaignId: Yup.string(),
  brandId: requiredString,
  status: Yup.string(),
  publishDate: Yup.date(),
  platforms: Yup.array(),
  tags: Yup.array(),
  assets: Yup.array(),
  references: Yup.array(),
});

export const userValidation = Yup.object({
  name: requiredString,
  email: requiredEmail,
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number and one special character'
    ),
  role: requiredString,
  status: Yup.string(),
});

export const roleValidation = Yup.object({
  name: requiredString,
  description: Yup.string(),
  permissions: Yup.array().of(Yup.string()),
});

// Utility functions for validation
export const validateForm = (values, schema) => {
  try {
    schema.validateSync(values, { abortEarly: false });
    return {};
  } catch (error) {
    if (error.inner) {
      return error.inner.reduce((errors, err) => {
        errors[err.path] = err.message;
        return errors;
      }, {});
    }
    return { form: error.message };
  }
};

export const validateField = (name, value, schema) => {
  try {
    schema.validateSyncAt(name, { [name]: value });
    return null;
  } catch (error) {
    return error.message;
  }
};

export default {
  // Yup validation schemas
  brandValidation,
  clientValidation,
  projectValidation,
  campaignValidation,
  contentValidation,
  userValidation,
  roleValidation,
  validateForm,
  validateField,
  
  // Standard validation functions
  isNotEmpty,
  isValidEmail,
  isValidUrl,
  isValidPhone,
  isNotPastDate,
  isEndDateAfterStartDate,
  isPositiveNumber,
  hasMinItems,
  getNestedValue,
  validateFormStep,
};